import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Play, Pause, VolumeX, Layers, Music2, Loader2,
  Download, Mic, Wand2, BarChart3, Share2, Lock, Unlock,
} from 'lucide-react';
import { toast } from 'sonner';
import WaveSurferPlayer from './WaveSurferPlayer';
import StructureTimeline from './StructureTimeline';
import StructureQuickActions from './StructureQuickActions';
import { useLang } from '../../context/LanguageContext';
import { API_BASE as API } from '../../lib/apiBase';
import { StudioCompanionBubble } from './StudioCompanionBubble';

const STEM_DEFS = [
  { id: 'drums',  name: 'DRUMS',  color: '#EA580C' },
  { id: 'bass',   name: 'BASS',   color: '#7C3AED' },
  { id: 'vocals', name: 'VOCALS', color: '#06B6D4' },
  { id: 'other',  name: 'OTHER',  color: '#F5C842' },
];

export default function ProducerDAWView({
  audioUrl, generating, taskPolling, genTimer = 0, genMessage = '',
  stages = {}, sessionId, token, onPublish, className = '',
  playerRef, loopRegion, sections, defaultSections, bpm,
  onSectionsChange, onLoopChange, onChatMessage,
  onSeekRequest = null, onAutoPlayPreview = false, onPlaybackTimeChange,
  compactEmptyState = false,
  lockedStems = { drums: false, bass: false, vocals: false, other: false },
  onLockedStemsChange,
  vocalLocked = true,
  onVocalLockedChange,
  showCompanionBubble = true,
}) {
  const { lang } = useLang();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const waveSurferRef = useRef(null);

  // Stems
  const [separating, setSeparating] = useState(false);
  const [stems, setStems] = useState(null);
  const [stemAudios, setStemAudios] = useState({});
  const [stemVolumes, setStemVolumes] = useState({ drums: 1, bass: 1, vocals: 1, other: 1 });
  const [stemMutes, setStemMutes] = useState({ drums: false, bass: false, vocals: false, other: false });
  const [soloStem, setSoloStem] = useState(null);
  const [stemView, setStemView] = useState(false);
  const [stemServiceStatus, setStemServiceStatus] = useState({
    available: true,
  });

  // Analysis
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  // Vocal recording
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [vocalUrl, setVocalUrl] = useState(null);
  const [correcting, setCorrecting] = useState(false);

  // Export
  const [exporting, setExporting] = useState(false);

  // Re-synthesis
  const [resynthStem, setResynthStem] = useState(null);
  const [resynthDesc, setResynthDesc] = useState('');
  const [resynthing, setResynthing] = useState(false);

  // Load stem audio elements
  useEffect(() => {
    if (!stems) return;
    const audios = {};
    STEM_DEFS.forEach(def => {
      if (stems[def.id]) {
        const audio = new Audio(`${API}${stems[def.id]}`);
        audio.crossOrigin = 'anonymous';
        audio.volume = stemVolumes[def.id] || 1;
        audios[def.id] = audio;
      }
    });
    setStemAudios(audios);
    return () => Object.values(audios).forEach(a => { a.pause(); a.src = ''; });
  }, [stems]);

  useEffect(() => {
    Object.entries(stemAudios).forEach(([id, audio]) => {
      audio.volume = soloStem ? (id === soloStem ? stemVolumes[id] : 0) : (stemMutes[id] ? 0 : stemVolumes[id]);
    });
  }, [stemVolumes, stemMutes, soloStem, stemAudios]);

  // Auto-analyze when audio loads
  useEffect(() => {
    if (audioUrl && !analysis && !analyzing) handleAnalyze();
  }, [audioUrl]);

  // Surface stem service availability to keep lab state explicit.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const checkStemStatus = async () => {
      try {
        const r = await fetch(`${API}/api/producer/stems/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json();
        if (!cancelled && r.ok) {
          setStemServiceStatus({
            available: !!data.available,
          });
        }
      } catch {
        if (!cancelled) {
          setStemServiceStatus({ available: false });
        }
      }
    };
    checkStemStatus();
    return () => { cancelled = true; };
  }, [token]);

  const seekMainToTime = useCallback((targetTime) => {
    const ws = waveSurferRef.current;
    if (!ws || typeof targetTime !== 'number' || Number.isNaN(targetTime)) return;
    const dur = ws.getDuration?.() || 0;
    if (!dur) return;
    const pct = Math.max(0, Math.min(1, targetTime / dur));
    ws.seekTo?.(pct);
  }, []);

  useEffect(() => {
    if (!audioUrl || stemView) return;
    seekMainToTime(onSeekRequest);
  }, [audioUrl, onSeekRequest, stemView, seekMainToTime]);

  const playAllStems = () => { Object.values(stemAudios).forEach(a => a.play().catch(() => {})); setIsPlaying(true); };
  const pauseAllStems = () => { Object.values(stemAudios).forEach(a => a.pause()); setIsPlaying(false); };

  const handleSeparateStems = async () => {
    if (!sessionId || !token || !audioUrl) return;
    setSeparating(true);
    try {
      const r = await fetch(`${API}/api/producer/stems/separate`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await r.json();
      if (data.success && data.stems) {
        setStems(data.stems);
        setStemView(true);
        toast.success(lang === 'es' ? 'Stems listos para mezclar' : 'Stems ready for mixing');
      } else {
        toast.error(data.detail || (lang === 'es' ? 'No se pudieron separar stems' : 'Could not separate stems'));
      }
    } catch (e) {
      console.error('Stem separation error:', e);
      toast.error(lang === 'es' ? 'Error separando stems' : 'Error separating stems');
    }
    setSeparating(false);
  };

  const handleAnalyze = async () => {
    if (!sessionId || !token) return;
    setAnalyzing(true);
    try {
      const r = await fetch(`${API}/api/producer/analyze-audio`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await r.json();
      if (data.success) setAnalysis(data);
    } catch (e) {
      console.error('Analysis error:', e);
    }
    setAnalyzing(false);
  };

  // Vocal recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      const chunks = [];
      mr.ondataavailable = (e) => chunks.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunks, { type: 'audio/webm' });
        // Upload and pitch-correct
        setCorrecting(true);
        const fd = new FormData();
        fd.append('audio', blob, 'vocal.webm');
        fd.append('session_id', sessionId);
        try {
          const r = await fetch(`${API}/api/producer/pitch-correct-vocal`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
          });
          const data = await r.json();
          if (data.success) setVocalUrl(`${API}${data.corrected_url}`);
        } catch (e) { console.error('Pitch correct error:', e); }
        setCorrecting(false);
      };
      mr.start();
      setMediaRecorder(mr);
      setRecording(true);
    } catch (e) { console.error('Mic error:', e); }
  };

  const stopRecording = () => {
    if (mediaRecorder) { mediaRecorder.stop(); setRecording(false); }
  };

  // Export mix
  const handleExport = async () => {
    if (!stems) return;
    setExporting(true);
    try {
      const r = await fetch(`${API}/api/producer/export-mix`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, volumes: stemVolumes }),
      });
      const data = await r.json();
      if (data.success && data.download_url) {
        const a = document.createElement('a');
        a.href = `${API}${data.download_url}`;
        a.download = `pulse_mix_${sessionId}.mp3`;
        a.click();
      }
    } catch (e) {
      console.error('Export error:', e);
      toast.error(lang === 'es' ? 'Error exportando mezcla' : 'Error exporting mix');
    }
    setExporting(false);
  };

  // Re-synthesize stem
  const toggleStemLock = (id) => {
    if (typeof onLockedStemsChange !== 'function') return;
    onLockedStemsChange((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleResynth = async () => {
    if (!resynthStem || !resynthDesc.trim()) return;
    if (lockedStems?.[resynthStem]) {
      toast.error(lang === 'es' ? 'Capa bloqueada. Desbloquea para re-sintetizar.' : 'Layer locked. Unlock to re-synthesize.');
      return;
    }
    setResynthing(true);
    try {
      const r = await fetch(`${API}/api/producer/resynthesize-stem`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, stem_name: resynthStem, description: resynthDesc }),
      });
      const data = await r.json();
      if (data.task_id) {
        // Poll for completion
        const pollResynth = setInterval(async () => {
          const pr = await fetch(`${API}/api/producer/task/${data.task_id}`, { headers: { Authorization: `Bearer ${token}` } });
          const pd = await pr.json();
          if (pd.status === 'done' && pd.audio_url) {
            clearInterval(pollResynth);
            setStems(prev => ({ ...prev, [resynthStem]: pd.audio_url }));
            setResynthStem(null); setResynthDesc(''); setResynthing(false);
            toast.success(lang === 'es' ? 'Stem re-sintetizado' : 'Stem re-synthesized');
          } else if (pd.status === 'error') {
            clearInterval(pollResynth); setResynthing(false);
            toast.error(lang === 'es' ? 'No se pudo re-sintetizar' : 'Could not re-synthesize stem');
          }
        }, 5000);
      }
    } catch (e) {
      console.error('Resynth error:', e);
      setResynthing(false);
      toast.error(lang === 'es' ? 'Error en re-síntesis' : 'Resynthesis failed');
    }
  };

  const fmt = (s) => { if (!s || isNaN(s)) return '0:00'; return `${Math.floor(s/60)}:${Math.floor(s%60).toString().padStart(2,'0')}`; };

  // ── Waiting state ──
  if (!audioUrl && !taskPolling && !generating) {
    return (
      <div className={`flex flex-col items-center ${compactEmptyState ? 'justify-start pt-3' : 'justify-start sm:justify-center pt-6 sm:pt-0'} h-full ${className}`} data-testid="daw-waiting">
        <div className={`${compactEmptyState ? 'w-10 h-10 mb-2' : 'w-12 h-12 sm:w-16 sm:h-16 mb-3 sm:mb-4'} rounded-2xl flex items-center justify-center`} style={{ background: 'linear-gradient(135deg, #F5C842, #FF6B00)' }}>
          <Music2 size={compactEmptyState ? 18 : 22} className="text-black sm:w-7 sm:h-7" />
        </div>
        <p className={`${compactEmptyState ? 'text-[11px]' : 'text-xs sm:text-sm'} text-[#71717A] text-center max-w-[240px] sm:max-w-[280px]`}>
          {lang === 'es' ? 'Describe tu cancion en el chat y PULSE la creara para ti' : 'Describe your song in the chat and PULSE will create it'}
        </p>
        <p className="text-[10px] text-[#52525B] text-center max-w-[260px] mt-1">
          {lang === 'es' ? 'El editor DAW aparece aqui al generar la base.' : 'The DAW editor appears here once the base is generated.'}
        </p>
      </div>
    );
  }

  // ── Generating state ──
  if (taskPolling || generating) {
    return (
      <div className={`flex flex-col items-center justify-center h-full ${className}`} data-testid="daw-generating">
        <div className="relative w-24 h-24 mb-6">
          <div className="absolute inset-0 rounded-full border-2 border-[#F5C842] animate-ping opacity-20" />
          <div className="absolute inset-2 rounded-full border-2 border-[#FF6B00] animate-ping opacity-15" style={{ animationDelay: '0.3s' }} />
          <div className="absolute inset-4 rounded-full border-2 border-[#F5C842] animate-ping opacity-10" style={{ animationDelay: '0.6s' }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Music2 size={28} className="text-[#F5C842] animate-pulse" />
          </div>
        </div>
        <p className="text-sm font-bold text-[#F5C842] mb-1">{lang === 'es' ? 'PULSE esta creando...' : 'PULSE is creating...'}</p>
        <p className="text-xs text-[#71717A] italic mb-2">{genMessage}</p>
        <div className="w-48 h-1 bg-[#1A1A1F] rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${Math.min((genTimer / 90) * 100, 95)}%`, background: 'linear-gradient(90deg, #F5C842, #FF6B00)' }} />
        </div>
        <p className="text-[10px] text-[#3F3F46] mt-1 font-mono">{genTimer}s</p>
      </div>
    );
  }

  // ── Audio ready ──
  return (
    <div className={`relative flex flex-col h-full ${className}`} data-testid="daw-ready">
      {showCompanionBubble && (
        <div className="pointer-events-none absolute right-2 top-2 z-30 max-w-[min(100%,340px)]">
          <StudioCompanionBubble visible stage={stemView ? 'stems' : 'daw'} hasStems={!!stems} />
        </div>
      )}
      {/* Transport + Analysis bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#1A1A1F] flex-shrink-0 flex-wrap">
        {/* Play/Pause */}
        {!stemView ? (
          <button data-testid="daw-play-btn" onClick={() => { if (waveSurferRef.current) waveSurferRef.current.playPause(); }}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-black flex-shrink-0" style={{ background: 'linear-gradient(135deg, #F5C842, #FF6B00)' }}>
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
          </button>
        ) : (
          <button data-testid="stems-play-btn" onClick={() => isPlaying ? pauseAllStems() : playAllStems()}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-black flex-shrink-0" style={{ background: 'linear-gradient(135deg, #F5C842, #FF6B00)' }}>
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
          </button>
        )}

        <span className="text-[10px] text-[#71717A] font-mono flex-shrink-0">{fmt(currentTime)}/{fmt(duration)}</span>
        <span className="text-[9px] text-[#52525B] px-2 py-1 rounded-md border border-[#1A1A1F] bg-[#0F0F12]">
          Band mix (master)
        </span>

        {/* BPM/Key badge */}
        {analysis && (
          <div data-testid="audio-analysis-badge" className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-[#1A1A1F] border border-[#F5C84220] flex-shrink-0">
            <BarChart3 size={10} className="text-[#F5C842]" />
            <span className="text-[9px] font-bold text-[#F5C842]">{analysis.bpm} BPM</span>
            <span className="text-[9px] text-[#71717A]">|</span>
            <span className="text-[9px] font-bold text-[#06B6D4]">{analysis.key_display}</span>
          </div>
        )}
        {analyzing && <Loader2 size={12} className="text-[#71717A] animate-spin flex-shrink-0" />}

        <div className="flex-1" />

        {/* Action buttons */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {/* Record vocal */}
          <button data-testid="record-vocal-btn" onClick={recording ? stopRecording : startRecording}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-bold transition-all ${
              recording ? 'bg-red-500 text-white animate-pulse' : correcting ? 'bg-[#06B6D420] text-[#06B6D4]' : 'bg-[#1A1A1F] text-[#71717A] hover:text-white border border-[#1A1A1F] hover:border-[#3F3F46]'
            }`} disabled={correcting}>
            <Mic size={10} /> {recording ? 'REC' : correcting ? 'Corrigiendo...' : (lang === 'es' ? 'Grabar voz' : 'Record')}
          </button>

          {/* Separate / Toggle stems */}
          {!stems && !separating && audioUrl && stemServiceStatus.available && (
            <button data-testid="separate-stems-btn" onClick={handleSeparateStems}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-bold bg-[#7C3AED15] text-[#7C3AED] border border-[#7C3AED30] hover:bg-[#7C3AED25] transition-all">
              <Layers size={10} /> {lang === 'es' ? 'Separar' : 'Separate'}
            </button>
          )}
          {!stemServiceStatus.available && (
            <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-bold bg-red-500/10 text-red-300 border border-red-500/30">
              <Layers size={10} />
              {lang === 'es' ? 'Stems no disponibles (Demucs)' : 'Stems unavailable (Demucs)'}
            </div>
          )}
          {separating && (
            <div className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-bold bg-[#7C3AED10] text-[#7C3AED]">
              <Loader2 size={10} className="animate-spin" /> {lang === 'es' ? 'Separando...' : 'Separating...'}
            </div>
          )}
          {stems && (
            <button data-testid="toggle-stem-view" onClick={() => setStemView(!stemView)}
              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-bold transition-all ${stemView ? 'bg-[#F5C84220] text-[#F5C842] border border-[#F5C84040]' : 'bg-[#1A1A1F] text-[#71717A] border border-[#1A1A1F]'}`}>
              <Layers size={10} /> {stemView ? 'Master' : (lang === 'es' ? 'Pistas' : 'Stems')}
            </button>
          )}

          {/* Export */}
          {stems && (
            <button data-testid="export-mix-btn" onClick={handleExport} disabled={exporting}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-bold bg-[#06B6D415] text-[#06B6D4] border border-[#06B6D430] hover:bg-[#06B6D425] transition-all">
              {exporting ? <Loader2 size={10} className="animate-spin" /> : <Download size={10} />} {lang === 'es' ? 'Exportar' : 'Export'}
            </button>
          )}

          {/* Publish */}
          {stages?.base_done && onPublish && (
            <button data-testid="daw-publish-btn" onClick={onPublish}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[9px] font-bold text-black" style={{ background: 'linear-gradient(135deg, #00D4FF, #0891B2)' }}>
              <Share2 size={10} /> {lang === 'es' ? 'Publicar' : 'Publish'}
            </button>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {/* Structure Timeline + Quick Actions */}
        {sections && sections.length > 0 && (
          <div className="mb-2 space-y-1">
            <StructureTimeline
              sections={sections}
              currentTime={currentTime}
              duration={duration}
              loopRegion={loopRegion}
              compact={false}
              editable={true}
              onSectionsChange={onSectionsChange}
              onLoopChange={onLoopChange}
            />
            <StructureQuickActions
              sections={sections}
              bpm={bpm || 120}
              defaultSections={defaultSections}
              onApply={onSectionsChange}
              onChat={onChatMessage}
            />
          </div>
        )}

        {!stemView ? (
          /* ── Single track + vocal ── */
          <div className="space-y-3" data-testid="daw-single-track">
            {/* Master track */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-8 rounded-full" style={{ background: '#F5C842' }} />
                <span className="text-[10px] font-bold text-[#71717A] tracking-wider">MASTER</span>
              </div>
              <div className="bg-[#0F0F12] rounded-xl border border-[#1A1A1F] overflow-hidden" style={{ minHeight: '120px' }}>
                <WaveSurferPlayer
                  ref={playerRef}
                  audioUrl={audioUrl}
                  autoPlay={onAutoPlayPreview}
                  height={120}
                  waveColor="#F5C84260"
                  progressColor="#F5C842"
                  cursorColor="#FF6B00"
                  loopRegion={loopRegion}
                  onReady={(info) => {
                    waveSurferRef.current = info;
                    if (info?.getDuration) setDuration(info.getDuration());
                    else if (info?.duration) setDuration(info.duration);
                    seekMainToTime(onSeekRequest);
                  }}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onTimeUpdate={(t) => {
                    const nextTime = typeof t === 'object' ? t.currentTime : t;
                    setCurrentTime(nextTime);
                    onPlaybackTimeChange?.(nextTime);
                  }}
                />
              </div>
            </div>

            {/* Vocal track (if recorded) */}
            {vocalUrl && (
              <div data-testid="vocal-track">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-8 rounded-full bg-pink-500" />
                  <span className="text-[10px] font-bold text-pink-400 tracking-wider">TU VOZ {analysis ? `(${analysis.key} ${analysis.mode})` : ''}</span>
                  {typeof onVocalLockedChange === 'function' && (
                    <button
                      type="button"
                      data-testid="vocal-protect-lock-master"
                      onClick={() => onVocalLockedChange(!vocalLocked)}
                      className={`ml-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-[8px] font-bold ${
                        vocalLocked ? 'bg-pink-500/20 text-pink-300' : 'bg-[#1A1A1F] text-[#71717A]'
                      }`}
                      title={lang === 'es' ? 'Proteger toma vocal al regenerar' : 'Protect vocal when regenerating'}
                    >
                      {vocalLocked ? <Lock size={10} /> : <Unlock size={10} />}
                      {lang === 'es' ? 'Proteger' : 'Protect'}
                    </button>
                  )}
                </div>
                <div className="bg-[#0F0F12] rounded-xl border border-pink-500/20 overflow-hidden" style={{ minHeight: '60px' }}>
                  <WaveSurferPlayer audioUrl={vocalUrl} height={60} waveColor="#EC489960" progressColor="#EC4899" cursorColor="#EC4899" />
                </div>
              </div>
            )}
          </div>
        ) : (
          /* ── Stem view ── */
          <div className="space-y-2" data-testid="daw-stem-tracks">
            {STEM_DEFS.map(def => {
              const url = stems?.[def.id];
              if (!url) return null;
              const isMuted = soloStem ? soloStem !== def.id : stemMutes[def.id];
              const isSolo = soloStem === def.id;

              return (
                <div key={def.id} data-testid={`stem-track-${def.id}`}
                  className="rounded-xl border p-3 transition-all"
                  style={{ borderColor: isMuted ? '#1A1A1F' : `${def.color}30`, background: isMuted ? '#0A0A0A' : `${def.color}05`, opacity: isMuted ? 0.4 : 1 }}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-2 h-6 rounded-full flex-shrink-0" style={{ background: def.color }} />
                    <span className="text-[10px] font-bold tracking-wider" style={{ color: isMuted ? '#3F3F46' : def.color }}>{def.name}</span>
                    <div className="flex-1" />

                    {/* Lock — PULSE will avoid regenerating this stem while locked */}
                    {typeof onLockedStemsChange === 'function' && (
                      <button
                        type="button"
                        data-testid={`stem-lock-${def.id}`}
                        onClick={() => toggleStemLock(def.id)}
                        className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                          lockedStems?.[def.id] ? 'bg-[#F5C84230] text-[#F5C842]' : 'bg-[#1A1A1F] text-[#71717A] hover:text-white'
                        }`}
                        title={lang === 'es' ? (lockedStems?.[def.id] ? 'Desbloquear capa' : 'Bloquear capa') : (lockedStems?.[def.id] ? 'Unlock layer' : 'Lock layer')}
                      >
                        {lockedStems?.[def.id] ? <Lock size={9} /> : <Unlock size={9} />}
                      </button>
                    )}

                    {/* Re-synth button */}
                    <button data-testid={`resynth-${def.id}`} onClick={() => { setResynthStem(def.id); setResynthDesc(''); }}
                      className="w-5 h-5 rounded flex items-center justify-center bg-[#1A1A1F] text-[#71717A] hover:text-[#F5C842] transition-colors disabled:opacity-40" title={lang === 'es' ? 'Re-sintetizar' : 'Re-synthesize'}
                      disabled={!!lockedStems?.[def.id]}>
                      <Wand2 size={9} />
                    </button>

                    <input type="range" min="0" max="2" step="0.05" value={stemVolumes[def.id]}
                      onChange={(e) => setStemVolumes(prev => ({ ...prev, [def.id]: parseFloat(e.target.value) }))}
                      className="w-14 h-1 accent-[#F5C842]" data-testid={`stem-volume-${def.id}`} />

                    <button data-testid={`stem-mute-${def.id}`}
                      onClick={() => { setSoloStem(null); setStemMutes(prev => ({ ...prev, [def.id]: !prev[def.id] })); }}
                      className={`w-5 h-5 rounded flex items-center justify-center text-[8px] font-bold ${stemMutes[def.id] ? 'bg-red-500/20 text-red-400' : 'bg-[#1A1A1F] text-[#71717A]'}`}>
                      {stemMutes[def.id] ? <VolumeX size={9} /> : 'M'}
                    </button>

                    <button data-testid={`stem-solo-${def.id}`}
                      onClick={() => setSoloStem(prev => prev === def.id ? null : def.id)}
                      className={`w-5 h-5 rounded flex items-center justify-center text-[8px] font-bold ${isSolo ? 'text-black' : 'bg-[#1A1A1F] text-[#71717A]'}`}
                      style={isSolo ? { background: def.color } : undefined}>S</button>
                  </div>

                  <div className="h-10 bg-[#0A0A0A] rounded-lg overflow-hidden">
                    <WaveSurferPlayer audioUrl={`${API}${url}`} height={40} waveColor={`${def.color}50`} progressColor={def.color} cursorColor={def.color} autoPlay={false} />
                  </div>
                </div>
              );
            })}

            {/* Vocal track in stem view */}
            {vocalUrl && (
              <div data-testid="vocal-track-stems" className="rounded-xl border border-pink-500/20 p-3" style={{ background: '#EC489905' }}>
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-2 h-6 rounded-full bg-pink-500" />
                  <span className="text-[10px] font-bold text-pink-400 tracking-wider">TU VOZ</span>
                  {typeof onVocalLockedChange === 'function' && (
                    <button
                      type="button"
                      data-testid="vocal-protect-lock"
                      onClick={() => onVocalLockedChange(!vocalLocked)}
                      className={`ml-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-[8px] font-bold ${
                        vocalLocked ? 'bg-pink-500/20 text-pink-300' : 'bg-[#1A1A1F] text-[#71717A]'
                      }`}
                      title={lang === 'es' ? 'Proteger toma vocal al regenerar' : 'Protect vocal when regenerating'}
                    >
                      {vocalLocked ? <Lock size={10} /> : <Unlock size={10} />}
                      {lang === 'es' ? 'Proteger' : 'Protect'}
                    </button>
                  )}
                </div>
                <div className="h-10 bg-[#0A0A0A] rounded-lg overflow-hidden">
                  <WaveSurferPlayer audioUrl={vocalUrl} height={40} waveColor="#EC489950" progressColor="#EC4899" cursorColor="#EC4899" autoPlay={false} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Re-synthesis modal */}
      {resynthStem && (
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 rounded-2xl" data-testid="resynth-modal">
          <div className="bg-[#0F0F12] border border-[#F5C84030] rounded-2xl p-5 w-80">
            <h3 className="text-sm font-bold mb-1 text-[#F5C842]">
              {lang === 'es' ? 'Re-sintetizar' : 'Re-synthesize'} {resynthStem.toUpperCase()}
            </h3>
            <p className="text-[10px] text-[#71717A] mb-3">
              {lang === 'es' ? 'Describe el nuevo sonido. El ritmo se mantiene.' : 'Describe the new sound. Rhythm is preserved.'}
            </p>
            <input data-testid="resynth-input" type="text" value={resynthDesc} onChange={(e) => setResynthDesc(e.target.value)}
              placeholder={lang === 'es' ? 'ej: pajaros cantando, cristales, zapatos...' : 'e.g. birds chirping, glass, shoes...'}
              className="w-full px-3 py-2 rounded-lg bg-[#1A1A1F] border border-[#3F3F46] text-sm text-white placeholder-[#3F3F46] mb-3 outline-none focus:border-[#F5C842]" />
            <div className="flex gap-2">
              <button onClick={() => setResynthStem(null)} className="flex-1 py-2 rounded-lg bg-[#1A1A1F] text-[#71717A] text-xs">
                {lang === 'es' ? 'Cancelar' : 'Cancel'}
              </button>
              <button data-testid="resynth-confirm" onClick={handleResynth} disabled={!resynthDesc.trim() || resynthing}
                className="flex-1 py-2 rounded-lg text-xs font-bold text-black disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #F5C842, #FF6B00)' }}>
                {resynthing ? <Loader2 size={12} className="animate-spin mx-auto" /> : (lang === 'es' ? 'Crear' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
